import { useState } from "react"
import { CloseButton, Dialog, Grid, GridItem, HStack, NativeSelect, Portal, Stack, Text } from "@chakra-ui/react"
import { Button, toaster } from "@/components"
import { registerDealForIrr, IrrDealManualFields } from "@/api/irr"
import { EsoDeal } from "@/types"

interface Props {
  deal: EsoDeal
  managers: { id: number; name: string }[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const today = () => new Date().toISOString().slice(0, 10)

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)

export const AddToIrrModal = ({ deal, managers, isOpen, onClose, onSuccess }: Props) => {
  const offer0 = deal.offers.find((o) => o.offerNumber === 0)

  // Compute summary stats from deal data
  const includedGrants =
    offer0?.offerGrants?.filter((og) => og.included === 1).map((og) => og.grant) ??
    deal.grants

  const totalVested = includedGrants.reduce((s, g) => s + g.vested, 0)
  const initialPayment = includedGrants.reduce((s, g) => s + g.vested * g.price + (g.tax ?? 0), 0)
  const additionalPayment = offer0?.desiredLiquidity ?? 0
  const totalPayment = initialPayment + additionalPayment
  const pp = offer0?.pp ?? 0
  const currentFmv = deal.company.currentFmv ? Number(deal.company.currentFmv) : null

  // Form state
  const [closingDate, setClosingDate] = useState(today())
  const [inceptionDate, setInceptionDate] = useState(deal.inceptionDate?.slice(0, 10) ?? "")
  const [creditScore, setCreditScore] = useState("")
  const [finalManagerId, setFinalManagerId] = useState("")
  const [backupManagerId, setBackupManagerId] = useState("")
  const [employeeCount, setEmployeeCount] = useState("")
  const [referralFee, setReferralFee] = useState("0")
  const [industry, setIndustry] = useState("")
  const [geography, setGeography] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!closingDate) {
      toaster.create({ title: "Closing date is required", type: "error", duration: 3000 })
      return
    }
    setSubmitting(true)
    try {
      const fields: IrrDealManualFields = {
        originalClosingDate: closingDate,
        inceptionDate: inceptionDate || undefined,
        creditScore: creditScore !== "" ? Number(creditScore) : null,
        finalManagerId: finalManagerId !== "" ? Number(finalManagerId) : null,
        backupManagerId: backupManagerId !== "" ? Number(backupManagerId) : null,
        transactionEmployeeCount: employeeCount !== "" ? Number(employeeCount) : null,
        referralFeeOutflow: referralFee !== "" ? Number(referralFee) : null,
        industry: industry || null,
        geography: geography || null,
      }
      await registerDealForIrr(deal.id, fields)
      toaster.create({ title: "Deal registered in IRR", type: "success", duration: 3000 })
      onSuccess()
      onClose()
    } catch {
      toaster.create({ title: "Failed to register deal for IRR", type: "error", duration: 3000 })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog.Root size="lg" open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="card.bg">
            <Dialog.Header px={6} pt={5} pb={3}>
              <Dialog.Title fontFamily="body" fontWeight="semibold">
                Add to IRR — {deal.company.name}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body px={6} pb={6}>
              <Stack gap={5}>
                {/* Computed summary */}
                <Stack gap={2} p={4} borderRadius="md" bg="hover.bg">
                  <Text fontSize="xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="wide">
                    Transaction Summary (auto-computed)
                  </Text>
                  <Grid templateColumns="1fr 1fr" gap={3}>
                    <GridItem>
                      <Text fontSize="xs" color="text.muted">Total Vested Shares</Text>
                      <Text fontWeight="semibold">{totalVested.toLocaleString()}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="text.muted">PP</Text>
                      <Text fontWeight="semibold">{pp > 0 ? `${pp}%` : "—"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="text.muted">Total Payment</Text>
                      <Text fontWeight="semibold">{totalPayment > 0 ? fmt(totalPayment) : "—"}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="text.muted">FMV at Transaction</Text>
                      <Text fontWeight="semibold">{currentFmv ? `$${currentFmv.toFixed(2)}` : "—"}</Text>
                    </GridItem>
                  </Grid>
                </Stack>

                {/* Manual fields */}
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Closing Date *</Text>
                      <input
                        type="date"
                        value={closingDate}
                        onChange={(e) => setClosingDate(e.target.value)}
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Inception Date</Text>
                      <input
                        type="date"
                        value={inceptionDate}
                        onChange={(e) => setInceptionDate(e.target.value)}
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Credit Score</Text>
                      <input
                        type="number"
                        value={creditScore}
                        onChange={(e) => setCreditScore(e.target.value)}
                        placeholder="e.g. 720"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Employee Count</Text>
                      <input
                        type="number"
                        value={employeeCount}
                        onChange={(e) => setEmployeeCount(e.target.value)}
                        placeholder="e.g. 500"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Final Manager</Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={finalManagerId}
                          onChange={(e) => setFinalManagerId(e.target.value)}
                        >
                          <option value="">— none —</option>
                          {managers.map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Backup Manager</Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={backupManagerId}
                          onChange={(e) => setBackupManagerId(e.target.value)}
                        >
                          <option value="">— none —</option>
                          {managers.map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Referral Fee Outflow ($)</Text>
                      <input
                        type="number"
                        value={referralFee}
                        onChange={(e) => setReferralFee(e.target.value)}
                        placeholder="0"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Industry</Text>
                      <input
                        type="text"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        placeholder="e.g. SaaS"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="medium">Geography</Text>
                      <input
                        type="text"
                        value={geography}
                        onChange={(e) => setGeography(e.target.value)}
                        placeholder="e.g. Bay Area"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--chakra-colors-border-default, #3a3a4a)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "inherit",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      />
                    </Stack>
                  </GridItem>
                </Grid>

                <HStack justify="flex-end" gap={3} pt={2}>
                  <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorPalette="purple"
                    onClick={handleSubmit}
                    loading={submitting}
                  >
                    Register in IRR
                  </Button>
                </HStack>
              </Stack>
            </Dialog.Body>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
